import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFocusEffect } from '@react-navigation/native';
import { useAllSpaceAccounts } from 'features/spaces/hooks/useAllSpaceAccounts';
import { selectSelectedSpace } from 'features/spaces/reducers/selectors';
import { useAppSelector } from 'store/hooks';
import { isWeb } from '../../../constants';
export const useExportDataState = (accountIds) => {
    const currentSpaceId = useAppSelector(selectSelectedSpace);
    const isFetchingSpacesInfo = useAppSelector((store) => store.exportData.isFetchingSpacesInfo);
    const [isLoadingState, setIsLoadingState] = useState(true);
    const spaceAccounts = useAllSpaceAccounts();
    const { spacesToShow, hiddenAccounts } = useMemo(() => {
        if (isFetchingSpacesInfo)
            return { spacesToShow: [], hiddenAccounts: false };
        let hiddenAccounts = false;
        const spacesToShow = Object.values(spaceAccounts).filter(({ space }) => {
            if (!space.accountsCount) {
                hiddenAccounts = true;
            }
            return !!space.accountsCount;
        });
        return { spacesToShow, hiddenAccounts };
    }, [isFetchingSpacesInfo, spaceAccounts]);
    const getInitialSelectedSpaces = useCallback(() => {
        let selectedSpaces = [];
        spacesToShow.forEach(({ space }) => {
            if (space.isDefault || space.isPremium) {
                selectedSpaces.push(space);
            }
        });
        if (accountIds?.length) {
            selectedSpaces = selectedSpaces.filter(({ id }) => id === currentSpaceId);
        }
        return selectedSpaces;
    }, [spacesToShow, accountIds, currentSpaceId]);
    const [selectedSpaces, setSelectedSpaces] = useState([]);
    const getSpacesWithAccounts = useCallback((selectedSpacesParam) => {
        const filteredSpacesWithAccounts = spacesToShow.filter(({ space }) => (selectedSpacesParam || selectedSpaces).find(({ id }) => space.id === id));
        const accountsToShow = filteredSpacesWithAccounts.reduce((acc, curr) => [
            ...acc,
            ...curr.accounts.filter((item) => !('isEmmaInvestmentAccount' in item || 'productId' in item)),
        ], []);
        return { filteredSpacesWithAccounts, accountsToShow };
    }, [selectedSpaces, spacesToShow]);
    const getInitialSelectedAccounts = useCallback((selectedSpacesParam) => {
        let selectedAccounts = getSpacesWithAccounts(selectedSpacesParam).accountsToShow;
        if (accountIds?.length) {
            selectedAccounts = selectedAccounts.filter(({ id, spaceId }) => typeof id === 'number' &&
                (accountIds.includes(id) ||
                    (!accountIds.length && spaceId === currentSpaceId)));
        }
        return selectedAccounts;
    }, [getSpacesWithAccounts, accountIds, currentSpaceId]);
    const [selectedAccounts, setSelectedAccounts] = useState([]);
    const wasInitialized = useRef(false);
    const [wasInitializedWeb, setWasInitializedWeb] = useState(false);
    useFocusEffect(useCallback(() => {
        if (isWeb) {
            wasInitialized.current = false;
            setWasInitializedWeb(false);
        }
    }, []));
    useEffect(() => {
        if (isFetchingSpacesInfo ||
            wasInitialized.current ||
            (isWeb && wasInitializedWeb)) {
            return;
        }
        const allAccountsFetched = spaceAccounts.every(({ space, accounts }) => space.accountsCount === accounts.length);
        if (!allAccountsFetched)
            return;
        wasInitialized.current = true;
        const initialSelectedSpaces = getInitialSelectedSpaces();
        setSelectedSpaces(initialSelectedSpaces);
        const initialSelectedAccounts = getInitialSelectedAccounts(initialSelectedSpaces);
        setSelectedAccounts(initialSelectedAccounts);
        setIsLoadingState(false);
        if (isWeb) {
            setWasInitializedWeb(true);
        }
    }, [
        getInitialSelectedAccounts,
        getInitialSelectedSpaces,
        isFetchingSpacesInfo,
        spaceAccounts,
        wasInitializedWeb,
    ]);
    const selectedAccountsMap = useMemo(() => selectedAccounts.reduce((acc, account) => ({ ...acc, [account.id]: account }), {}), [selectedAccounts]);
    const onSaveSpaces = useCallback((spaces) => {
        const addedSpaceIds = new Set();
        setSelectedSpaces((prev) => {
            spaces.forEach((space) => {
                if (!prev.find((s) => s.id === space.id)) {
                    addedSpaceIds.add(space.id);
                }
            });
            return spaces;
        });
        const newSelectedSpaces = spaces.reduce((acc, curr) => ({ ...acc, [curr.id]: curr }), {});
        const allVisibleAccounts = spacesToShow.reduce((acc, curr) => [
            ...acc,
            ...curr.accounts.filter((item) => !('isEmmaInvestmentAccount' in item || 'productId' in item)),
        ], []);
        setSelectedAccounts((prev) => prev
            .filter(({ spaceId }) => !!newSelectedSpaces[spaceId || -1])
            .concat(allVisibleAccounts.filter(({ spaceId }) => addedSpaceIds.has(spaceId || -1))));
    }, [spacesToShow]);
    const onSaveAccounts = useCallback((accounts) => {
        setSelectedAccounts(accounts);
    }, []);
    const { filteredSpacesWithAccounts, accountsToShow } = useMemo(getSpacesWithAccounts, [getSpacesWithAccounts]);
    return {
        isLoadingState,
        onSaveAccounts,
        onSaveSpaces,
        hiddenAccounts,
        selectedSpaces,
        selectedAccounts,
        selectedAccountsMap,
        accountsToShow,
        spacesToShow,
        filteredSpacesWithAccounts,
    };
};
